import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSession } from "next-auth/react";
import { getCookie, setCookie } from "cookies-next";
import { listenToFirestoreMeetings } from "@/store/apps/meetings/MeetingThunks";
import {
  completeEnrichment,
  fetchUserData,
} from "@/store/apps/userData/UserDataSlice";
import { store } from "@/store/store";
import EnrichmentLoadingScreen from "./EnrichmentLoadingScreen";

// RC Live enrichment (fresh LinkedIn scrape) can take up to 60s. The notifier
// hides immediately when the server signals completion via Pusher or when
// requiresManualInput flips true (handing off to VanityModal). These timers
// are only safety nets so a stuck request doesn't trap the user forever.
const MAX_SHOW_TIME = 70000; // 70s — must exceed RC Live's 60s timeout so Pusher arrives first
const FALLBACK_TIMEOUT = 80000; // absolute safety net
const EMERGENCY_EXIT_DELAY = 15000; // user can dismiss after 15s if stuck

const isUsablePicture = (value) => {
  if (typeof value !== "string" || !value.trim()) return false;
  const normalized = value.trim().toLowerCase();
  return ![
    "default",
    "default-avatar",
    "default_avatar",
    "profile-default",
    "profile_default",
  ].some((marker) => normalized.includes(marker));
};

export default function EnrichmentNotifier({ onActiveChange = () => {} }) {
  const dispatch = useDispatch();

  const { data: session } = useSession();
  const userSub = session?.user?.sub;
  const messageFirstSignup = ["google", "email"].includes(
    session?.user?.signupProvider,
  );
  const userName = session?.user?.given_name || session?.user?.name;
  const profile = useSelector((state) => state.user.profile);
  // Raw Google/session images can be generated letter avatars (for example,
  // the lowercase "a" fallback). Only use a cached profile photo here. The
  // loader's orb is the intentional fallback while onboarding is incomplete.
  const userImage = isUsablePicture(profile?.picture_cached)
    ? profile.picture_cached
    : null;

  const [showNotification, setShowNotification] = useState(false);
  const [introStarted, setIntroStarted] = useState(false);
  const [introTextComplete, setIntroTextComplete] = useState(false);
  const [devTestActive, setDevTestActive] = useState(() => {
    if (typeof window === "undefined") return false;
    if (process.env.NODE_ENV !== "development") return false;
    return new URLSearchParams(window.location.search).has("testEnrichment");
  });
  const [forceHide, setForceHide] = useState(false);
  const [initialDelayComplete, setInitialDelayComplete] = useState(false);
  const [allowEmergencyExit, setAllowEmergencyExit] = useState(false);

  const showTimeRef = useRef(null);
  const fallbackTimeoutRef = useRef(null);
  const emergencyTimerRef = useRef(null);
  const hasShownRef = useRef(false);
  const hasCompletedRef = useRef(false);
  const enrichmentStartTimeRef = useRef(null);
  const handoffTimerRef = useRef(null);

  const [handingOff, setHandingOff] = useState(false);

  const [showEarlyLoading, setShowEarlyLoading] = useState(() => {
    // During SSR, cookies are not available in the browser context so getCookie
    // returns undefined → default to false to avoid a hydration-flash on every
    // page load for returning users. The useEffect below re-checks immediately
    // after client mount and sets it correctly for first-time users.
    if (typeof window === "undefined") return false;
    return getCookie("meex_enrichment_complete") !== "true";
  });

  // Re-validate the cookie on client mount. useLayoutEffect fires before the
  // browser paints, so new users see the backdrop from frame zero (no gap).
  useLayoutEffect(() => {
    if (getCookie("meex_enrichment_complete") !== "true") {
      setShowEarlyLoading(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [hasReceivedInitialData, setHasReceivedInitialData] = useState(false);

  const enrichmentStatus = useSelector((state) => state.user.enrichmentStatus);
  const needsEnrichment = useSelector((state) => state.user.needsEnrichment);
  const isInitializationComplete = useSelector(
    (state) => state.user.isInitializationComplete,
  );
  const portfolioMigrated = useSelector(
    (state) => state.user.portfolioMigrated,
  );
  const requiresManualInput = useSelector(
    (state) => state.user.requiresManualInput,
  );
  const isUnderReview = useSelector((state) => state.user.isUnderReview);
  const verificationHandoffActive = useSelector(
    (state) => state.user.verificationHandoffActive,
  );
  const contactEmailConfirmationRequested = useSelector(
    (state) => state.user.contactEmailConfirmationRequested,
  );
  const contactEmailConfirmationCompleted = useSelector(
    (state) => state.user.contactEmailConfirmationCompleted,
  );
  const emailConfirmationPending =
    contactEmailConfirmationRequested ||
    (isInitializationComplete &&
      profile?.email_acknowledged === false &&
      Boolean(profile?.email) &&
      !isUnderReview);
  const emailAcknowledgedHandoffComplete =
    contactEmailConfirmationCompleted ||
    (messageFirstSignup && profile?.email_acknowledged === true);

  // Email/Google users must see the code modal first. The enrichment loader
  // starts only after LinkedIn message verification dispatches startEnrichment.
  const showMessageSignupLoader =
    messageFirstSignup &&
    (enrichmentStatus === "processing" || verificationHandoffActive) &&
    !requiresManualInput;

  // Calculate derived values first
  const isEnrichmentComplete =
    !verificationHandoffActive &&
    (enrichmentStatus === "completed" ||
      isInitializationComplete ||
      portfolioMigrated === true);

  const hasUserData =
    isEnrichmentComplete ||
    portfolioMigrated === true ||
    needsEnrichment === false ||
    profile?.linkedin?.id ||
    profile?.id;

  const shouldShow =
    !forceHide &&
    !hasCompletedRef.current &&
    !emailConfirmationPending &&
    !emailAcknowledgedHandoffComplete &&
    (!messageFirstSignup ||
      showMessageSignupLoader ||
      verificationHandoffActive) &&
    // Once we know the next step is the manual-URL modal, hand off immediately
    // so VanityModal opens in the same render frame the loader unmounts —
    // otherwise the user sees the dashboard for a few seconds in between.
    !requiresManualInput &&
    // If the user is already under admin review, skip the loader entirely —
    // the review modal opens directly without any loading screen.
    !isUnderReview &&
    enrichmentStatus !== "failed" &&
    (verificationHandoffActive ||
      (hasReceivedInitialData &&
        needsEnrichment === true &&
        !isEnrichmentComplete &&
        initialDelayComplete));

  // True only when Redux has confirmed this user actually needs enrichment —
  // distinguishes a quick returning-user flash (Case 1) from a real new-user wait (Case 2).
  const isConfirmedNewUser = hasReceivedInitialData && needsEnrichment === true;

  // True during the window between component mount and the first fetchUserData
  // response. Only applies to first-time users (showEarlyLoading is false when
  // the meex_enrichment_complete cookie is set, i.e. returning users).
  const isLoadingUserState =
    showEarlyLoading && !hasUserData && needsEnrichment === null && !forceHide;

  useEffect(() => {
    if (shouldShow || devTestActive) {
      setIntroStarted(true);
      setIntroTextComplete(false);
    }
  }, [shouldShow, devTestActive]);

  const keepIntroVisible =
    !emailAcknowledgedHandoffComplete &&
    !isEnrichmentComplete &&
    !hasCompletedRef.current &&
    introStarted &&
    !introTextComplete;

  const handleIntroTextComplete = useCallback(() => {
    setIntroTextComplete(true);
  }, []);

  const isLoaderVisible =
    !emailAcknowledgedHandoffComplete &&
    !hasCompletedRef.current &&
    (!messageFirstSignup ||
      showMessageSignupLoader ||
      verificationHandoffActive) &&
    (shouldShow ||
      devTestActive ||
      (isLoadingUserState && !messageFirstSignup));

  // Force hide function - wrapped in useCallback to prevent ESLint warnings
  const forceHideNotification = useCallback(() => {
    setShowNotification(false);
    setShowEarlyLoading(false);
    setForceHide(true);
    setAllowEmergencyExit(false);
    setIntroStarted(false);
    setIntroTextComplete(true);

    // Clear all timeouts
    if (showTimeRef.current) {
      clearTimeout(showTimeRef.current);
      showTimeRef.current = null;
    }
    if (fallbackTimeoutRef.current) {
      clearTimeout(fallbackTimeoutRef.current);
      fallbackTimeoutRef.current = null;
    }
    if (emergencyTimerRef.current) {
      clearTimeout(emergencyTimerRef.current);
      emergencyTimerRef.current = null;
    }

    // Reset enrichment tracking
    enrichmentStartTimeRef.current = null;

    // Set completion cookie as fallback
    setCookie("meex_enrichment_complete", "true", {
      maxAge: 60 * 60 * 24 * 365,
    });

    // Start meetings if we have a session
    if (userSub) {
      dispatch(listenToFirestoreMeetings(userSub));
    }
  }, [userSub, dispatch]);

  // Handle backdrop click — always blocked. Previously we had an emergency
  // exit after 15s, but users would accidentally click through and land on
  // a half-loaded dashboard. The fallback timeout still hides the loader if
  // it ever genuinely gets stuck.
  const handleBackdropClick = () => {
    return;
  };

  // Track when enrichment starts and set emergency timer
  useEffect(() => {
    if (shouldShow && !enrichmentStartTimeRef.current) {
      enrichmentStartTimeRef.current = Date.now();
      setAllowEmergencyExit(false);

      // Set emergency exit timer
      emergencyTimerRef.current = setTimeout(() => {
        setAllowEmergencyExit(true);
      }, EMERGENCY_EXIT_DELAY);

      return () => {
        if (emergencyTimerRef.current) {
          clearTimeout(emergencyTimerRef.current);
          emergencyTimerRef.current = null;
        }
      };
    }
  }, [shouldShow]);

  // Reset emergency exit when enrichment completes normally
  useEffect(() => {
    if (isEnrichmentComplete || !shouldShow) {
      enrichmentStartTimeRef.current = null;
      setAllowEmergencyExit(false);
      if (emergencyTimerRef.current) {
        clearTimeout(emergencyTimerRef.current);
        emergencyTimerRef.current = null;
      }
    }
  }, [isEnrichmentComplete, shouldShow]);

  // Absolute fallback timeout - always hide after 10 seconds
  useEffect(() => {
    if (showNotification && !fallbackTimeoutRef.current) {
      fallbackTimeoutRef.current = setTimeout(() => {
        forceHideNotification();
      }, FALLBACK_TIMEOUT);
    }

    return () => {
      if (fallbackTimeoutRef.current) {
        clearTimeout(fallbackTimeoutRef.current);
        fallbackTimeoutRef.current = null;
      }
    };
  }, [showNotification, forceHideNotification]);

  useEffect(() => {
    if (
      (enrichmentStatus && enrichmentStatus !== "idle") ||
      profile?.sub ||
      isInitializationComplete === true ||
      portfolioMigrated === true ||
      needsEnrichment === true ||
      (needsEnrichment === false && profile?.sub)
    ) {
      setHasReceivedInitialData(true);
    }
  }, [
    enrichmentStatus,
    needsEnrichment,
    profile?.sub,
    isInitializationComplete,
    portfolioMigrated,
  ]);

  useEffect(() => {
    setTimeout(() => setInitialDelayComplete(true), 100);
  }, []);

  useEffect(() => {
    if (isEnrichmentComplete) {
      hasCompletedRef.current = true;
      setShowEarlyLoading(false);
    }
  }, [isEnrichmentComplete]);

  useEffect(() => {
    if (hasReceivedInitialData && !initialDelayComplete) {
      setInitialDelayComplete(true);
    }
  }, [hasReceivedInitialData, initialDelayComplete]);

  useEffect(() => {
    if ((isEnrichmentComplete || portfolioMigrated) && userSub) {
      setCookie("meex_enrichment_complete", "true", {
        maxAge: 60 * 60 * 24 * 365,
      });

      forceHideNotification();
    }
  }, [isEnrichmentComplete, portfolioMigrated, userSub, forceHideNotification]);

  useEffect(() => {
    if (shouldShow && !isEnrichmentComplete) {
      setShowNotification(true);
      hasShownRef.current = true;

      // Record when we started showing
      showTimeRef.current = setTimeout(() => {
        forceHideNotification();
      }, MAX_SHOW_TIME);

      return () => {
        if (showTimeRef.current) {
          clearTimeout(showTimeRef.current);
          showTimeRef.current = null;
        }
      };
    } else {
      setShowNotification(false);
      if (userSub && hasUserData && hasReceivedInitialData) {
        dispatch(listenToFirestoreMeetings(userSub));
      }
    }
  }, [
    shouldShow,
    isEnrichmentComplete,
    userSub,
    dispatch,
    hasUserData,
    hasReceivedInitialData,
    forceHideNotification,
  ]);

  // Additional safeguard: if we've shown for too long, force hide.
  // BUT only when we have a definitive next-step signal (enrichment
  // completed OR manual input required). Otherwise we'd hide the loader
  // mid-enrichment and leak the dashboard before the next modal opens.
  useEffect(() => {
    if (
      hasShownRef.current &&
      hasUserData &&
      (isEnrichmentComplete || requiresManualInput)
    ) {
      const safeguardTimeout = setTimeout(() => {
        forceHideNotification();
      }, 0);

      return () => clearTimeout(safeguardTimeout);
    }
  }, [
    hasUserData,
    isEnrichmentComplete,
    requiresManualInput,
    forceHideNotification,
  ]);

  useEffect(() => {
    if (hasUserData && !isEnrichmentComplete && !hasCompletedRef.current) {
      setForceHide(false);
    }
  }, [hasUserData, isEnrichmentComplete]);

  useEffect(() => {
    if (verificationHandoffActive) {
      hasCompletedRef.current = false;
      setForceHide(false);
      setShowEarlyLoading(false);
      setShowNotification(true);
      hasShownRef.current = true;
    }
  }, [verificationHandoffActive]);

  // Pusher is the fast path, but an event can be missed while a tab is
  // connecting or resuming from the background. Re-read the persisted user
  // state during the handoff so refresh is never required to close the loader.
  useEffect(() => {
    if (!verificationHandoffActive || !userSub) return undefined;

    let cancelled = false;
    const recoverCompletion = async () => {
      await dispatch(fetchUserData(true, { silent: true }));
      if (cancelled) return;

      const state = store.getState().user;
      if (
        state.isInitializationComplete === true ||
        state.portfolioMigrated === true ||
        state.needsEnrichment === false
      ) {
        dispatch(completeEnrichment({ profile: state.profile }));
      }
    };

    recoverCompletion();
    const timer = setInterval(recoverCompletion, 5000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [dispatch, userSub, verificationHandoffActive]);

  // Dev-only test mode: visit any page with ?testEnrichment to preview the loader
  useEffect(() => {
    if (!devTestActive) return;
    hasShownRef.current = true;
    const completion = setTimeout(() => {
      setDevTestActive(false);
    }, 40000);
    return () => clearTimeout(completion);
  }, [devTestActive]);

  useEffect(() => {
    onActiveChange(shouldShow);
  }, [shouldShow, onActiveChange]);

  // Case 3: when requiresManualInput flips true after we've been visible, keep the
  // backdrop alive for 400ms so the user doesn't see a dashboard flash before
  // VanityModal opens (DashboardLayout delays it by 350ms).
  useEffect(() => {
    if (requiresManualInput && hasShownRef.current && !handingOff) {
      setHandingOff(true);
      handoffTimerRef.current = setTimeout(() => {
        setHandingOff(false);
      }, 400);
    }
    return () => {
      if (handoffTimerRef.current) {
        clearTimeout(handoffTimerRef.current);
        handoffTimerRef.current = null;
      }
    };
  }, [requiresManualInput, handingOff]);

  // Clean up timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeRef.current) clearTimeout(showTimeRef.current);
      if (fallbackTimeoutRef.current) clearTimeout(fallbackTimeoutRef.current);
      if (emergencyTimerRef.current) clearTimeout(emergencyTimerRef.current);
      if (handoffTimerRef.current) clearTimeout(handoffTimerRef.current);
    };
  }, []);

  if (emailAcknowledgedHandoffComplete && !devTestActive) return null;

  if (
    (!shouldShow &&
      !handingOff &&
      !devTestActive &&
      !verificationHandoffActive &&
      !keepIntroVisible) ||
    (isEnrichmentComplete && !devTestActive && !keepIntroVisible)
  )
    return null;

  return (
    <EnrichmentLoadingScreen
      showDetails={shouldShow || devTestActive || keepIntroVisible}
      userName={userName}
      userImage={devTestActive || messageFirstSignup ? null : userImage}
      messageFirstSignup={messageFirstSignup}
      onBackdropClick={handleBackdropClick}
      onIntroTextComplete={handleIntroTextComplete}
    />
  );
}
